import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {ValueLiteral} from '@mintdata-wsp/common/controllers/spreadsheet/formula/ast/FormulaASTNodes';
import {FormulaEngine} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/FormulaEngine';
import {OhmParseResult} from '@mintdata-wsp/common/controllers/spreadsheet/formula/engine/OhmParseResult';
import {ICellIOContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/CellIOContext';
import {DesignStateFromJsonCtx} from '@mintdata-wsp/common/state/ui/visualDesigner/DesignState';
import {Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotStatus} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotStatus';
import {SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {SlotUtil} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotUtil';
import {Workbook} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Workbook';
import {SheetId, SlotId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {Frames} from '@mintdata/mint-kit/api/values/frames/Frames';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {deserialize, serialize} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import {action, comparer, computed, IReactionDisposer, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export type SlotSnapshotFormula = any; //TODO: ideally should be smth like this: FormulaString | SerializedIValue
export const APPEND_RECORDS_COUNT_LIMIT = 32000;

export interface ISlotSnapshot
{
    id:SlotId;
    formula:SlotSnapshotFormula;
    location:SlotLocation;
}

export class Slot extends AnySlot
{
    @observable
    public formula:SlotFormula;

    @observable
    public location:SlotLocation;

    @observable
    public status:SlotStatus = null;

    public cellIO:ICellIOContext = null;

    public formulaReactionDisposer:IReactionDisposer = null;

    @observable
    private _value:IValue = null;

    private constructor()
    {
        super();
    }

    public static create(sheet:Sheet,
                         formula:SlotFormula,
                         location:SlotLocation):Slot
    {
        const slot:Slot = new Slot();

        slot.id = IdUtil.newGUID();
        slot.sheet = sheet;
        slot.formula = formula;
        slot.location = location;

        return slot;
    }

    public get workbook():Workbook
    {
        return this.sheet.workbook;
    }

    @computed
    public get value():IValue
    {
        if( SlotUtil.isIValue(this.formula) )
            return this.formula as IValue;
        else
            return this._value;
    }

    public get internalValue():IValue
    {
        return this._value;
    }

    @computed
    public get isSimpleValue():boolean
    {
        return !!this.formulaParseResult && this.formulaParseResult.rootNode instanceof ValueLiteral;
    }

    @computed
    public get isLazy():boolean
    {
        return !!this.formulaParseResult && this.formulaParseResult.isLazyFormula();
    }

    @computed
    public get isLive():boolean
    {
        return !!this.formulaParseResult && this.formulaParseResult.isLiveFormula();
    }

    @computed
    public get isAccumulative():boolean
    {
        return !!this.formulaParseResult && this.formulaParseResult.isAccumulative();
    }

    @computed({equals: comparer.structural})
    public get formulaParseResult():OhmParseResult
    {
        return FormulaEngine.getInstance().parseRC(
            {
                workbookId: this.workbook.id,
                sheetId: this.sheet.id,
                colIndex: this.location.colIndex,
                rowIndex: this.location.rowIndex,
                findSheetIdByName: (workbookId:WorkbookId, sheetName:string):SheetId =>
                {
                    const {workbook} = commonControllers.spreadsheets;
                    const sheet:Sheet = workbook.findSheetByName(workbookId, sheetName) as Sheet;

                    return sheet ? sheet.id : null;
                }
            },
            this.formula
        );
    }

    @action
    public setFormula(formula:SlotFormula):void
    {
        this.formula = formula;
    }

    @action
    public setLocation(location:SlotLocation):void
    {
        this.location = location;
    }

    @action
    public setValue(value:IValue):void
    {
        this._value = value;
    }

    @action
    public setStatus(status:SlotStatus):void
    {
        this.status = status;
    }

    @action
    public addValue(value:IValue):boolean
    {
        // Performance: Frame serialization is slow and has a hit on performance
        // mk.debug(area.Slot, `======@[ADD_VALUE]=======> Slot ${this._slot.location.toA1String()}: ${this.value} => ${value}`);

        // todo: Currently the value is added only for the IFrame data type

        if( value.isFrame )
        {
            if( this._value === null )
                this._value = Frames.empty();

            if( this._value.isFrame )
            {
                this._value = (this._value as IFrame).append(value as IFrame, APPEND_RECORDS_COUNT_LIMIT);
                return true;
            }
        }

        return false;
    }

    public clone(sheet:Sheet):Slot
    {
        const clonedSlot:Slot = new Slot();

        clonedSlot.id = IdUtil.newGUID();
        clonedSlot.sheet = sheet;

        clonedSlot.formula = typeof this.formula === 'string'
            ? this.formula
            : Type.deserializeIValue(serialize(this.formula));

        clonedSlot.location = this.location.clone();

        return clonedSlot;
    }

    public static toJson:ITransformer<Slot, ISlotSnapshot> = createTransformer((slot:Slot) =>
    {
        return {
            id: slot.id,
            sheetId: slot.sheet.id,
            formula: typeof slot.formula === 'string' ? slot.formula : serialize(slot.formula),
            location: serialize(slot.location)
        };
    });

    public static fromJson(json:ISlotSnapshot,
                           sheet:Sheet,
                           currentState?:Slot,
                           currentJson?:ISlotSnapshot,
                           ctx?:DesignStateFromJsonCtx):Slot
    {
        if( !currentState )
            return Slot.fromJsonDeep(json, sheet);

        if( !currentJson )
            currentJson = Slot.toJson(currentState);

        const slot:Slot = new Slot();

        slot.id = json.id;
        slot.sheet = sheet;

        slot._value = currentState._value;

        let isSlotChanged:boolean = false;

        if( json.formula === currentJson.formula )
            slot.formula = currentState.formula;
        else
        {
            slot.formula = typeof json.formula === 'string'
                ? json.formula
                : Type.deserializeIValue(json.formula);

            isSlotChanged = true;
        }

        if( json.location === currentJson.location )
            slot.location = currentState.location;
        else
        {
            slot.location = deserialize(SlotLocation, json.location);
            isSlotChanged = true;
        }

        if( isSlotChanged )
        {
            ctx.removedSlots.push(currentState);
            ctx.addedSlots.push(slot);
        }

        return slot;
    }

    public static fromJsonDeep(json:ISlotSnapshot, sheet:Sheet):Slot
    {
        const slot:Slot = new Slot();

        slot.id = json.id;
        slot.sheet = sheet;
        slot.formula = typeof json.formula === 'string' ? json.formula : Type.deserializeIValue(json.formula);
        slot.location = deserialize(SlotLocation, json.location);

        return slot;
    }
}
